import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  ApproveSimulationRequest,
  AttributeMappingRequest,
  AttributeMappingResponse,
  ColumnMetadataResponse,
  ConfigurationRuleRequest,
  ConfigurationRuleResponse,
  DataSourceMappingRequest,
  DataSourceMappingResponse,
  DealerDetailsResponse,
  FieldDefinitionResponse,
  HeliosDataSourceResponse,
  OperatorResponse,
  PalRuleHistoryResponse,
  PalRuleResponse,
  PalTypeResponse,
  RejectSimulationRequest,
  RuleMappingRequest,
  RuleMappingResponse,
  SimulationRequest,
  SimulationResponse,
  SubmitSimulationRequest,
  UserRoleResponse,
  UserDealersResponse,
} from "@/types/api";

const BASE_URL =
  import.meta.env.VITE_DCM_API_BASE_URL ?? "http://localhost:8080";

type DealerScopedArgs = {
  authoringDealerCode: string;
};

type SimulationScopedArgs = DealerScopedArgs & {
  simulationId: string;
};

type RuleScopedArgs = SimulationScopedArgs & {
  ruleId: string;
};

export type PalRuleScopedArgs = DealerScopedArgs & {
  palRuleId: string;
};

export type GetPalRulesArgs = DealerScopedArgs & {
  workstream?: string;
  description?: string;
};

export type GetPalDescriptionsArgs = DealerScopedArgs & {
  workstream: string;
};

export type GetPalAsOfDateArgs = DealerScopedArgs & {
  asOfDate: string;
};

export type GetOperatorsArgs = {
  dataType?: string;
};

export type GetFieldDefinitionsArgs = DealerScopedArgs & {
  workstream?: string;
  dataType?: string;
  onlyWithAllowedValues?: boolean;
};

export type GetFieldDefinitionArgs = DealerScopedArgs & {
  fieldName: string;
};

export type GetUserRolesArgs = {
  userName: string;
  dealerCode: string;
};

export type CheckUserRoleArgs = GetUserRolesArgs & {
  roleName: string;
};

export type GetUserDealersArgs = {
  userName: string;
};

const buildQueryParams = (
  params: Record<string, string | number | boolean | undefined>,
) => {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] =>
      entry[1] !== undefined && entry[1] !== null,
  );

  return entries.length ? Object.fromEntries(entries) : undefined;
};

// Get access token from localStorage (stored by Blocks Frame / MSAL)
const getAccessToken = (): string | null => {
  return localStorage.getItem("token");
};

export const dcmApi = createApi({
  reducerPath: "dcmApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    "PalRules",
    "Simulations",
    "SimulationRules",
    "Metadata",
    "UserRoles",
    "PalTypes",
    "DataSourceMappings",
    "HeliosDataSources",
    "AttributeMappings",
    "RuleMappings",
    "Dealers",
  ],
  endpoints: (builder) => ({
    getPalRules: builder.query<PalRuleResponse[], GetPalRulesArgs>({
      query: ({ authoringDealerCode, workstream, description }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules`,
        params: buildQueryParams({ workstream, description }),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result
                .filter((rule) => Boolean(rule.palRuleId))
                .map((rule) => ({
                  type: "PalRules" as const,
                  id: rule.palRuleId as string,
                })),
              { type: "PalRules" as const, id: "LIST" },
            ]
          : [{ type: "PalRules" as const, id: "LIST" }],
    }),
    getPalRule: builder.query<PalRuleResponse, PalRuleScopedArgs>({
      query: ({ authoringDealerCode, palRuleId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/${palRuleId}`,
      }),
      providesTags: (_, __, arg) => [
        { type: "PalRules" as const, id: arg.palRuleId },
      ],
    }),
    getPalRuleHistory: builder.query<
      PalRuleHistoryResponse[],
      PalRuleScopedArgs
    >({
      query: ({ authoringDealerCode, palRuleId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/${palRuleId}/history`,
      }),
      providesTags: [{ type: "PalRules", id: "HISTORY" }],
    }),
    getAllPalRulesHistory: builder.query<
      PalRuleHistoryResponse[],
      DealerScopedArgs
    >({
      query: ({ authoringDealerCode }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/history`,
      }),
      providesTags: [{ type: "PalRules", id: "ALL_HISTORY" }],
    }),
    getPalWorkstreams: builder.query<string[], DealerScopedArgs>({
      query: ({ authoringDealerCode }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/workstreams`,
      }),
      providesTags: [{ type: "PalRules", id: "WORKSTREAMS" }],
    }),
    getPalDescriptions: builder.query<string[], GetPalDescriptionsArgs>({
      query: ({ authoringDealerCode, workstream }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/workstreams/${encodeURIComponent(
          workstream,
        )}/descriptions`,
      }),
      providesTags: [{ type: "PalRules", id: "DESCRIPTIONS" }],
    }),
    getPalRuleCount: builder.query<
      number,
      DealerScopedArgs & { workstream?: string }
    >({
      query: ({ authoringDealerCode, workstream }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/count`,
        params: buildQueryParams({ workstream }),
      }),
      providesTags: [{ type: "PalRules", id: "COUNT" }],
    }),
    getPalRulesAsOfDate: builder.query<
      PalRuleHistoryResponse[],
      GetPalAsOfDateArgs
    >({
      query: ({ authoringDealerCode, asOfDate }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/palRules/asOfDate`,
        params: { asOfDate },
      }),
      providesTags: [{ type: "PalRules", id: "AS_OF_DATE" }],
    }),
    getSimulations: builder.query<SimulationResponse[], DealerScopedArgs>({
      query: ({ authoringDealerCode }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result
                .filter((simulation) => Boolean(simulation.simulationId))
                .map((simulation) => ({
                  type: "Simulations" as const,
                  id: simulation.simulationId as string,
                })),
              { type: "Simulations" as const, id: "LIST" },
            ]
          : [{ type: "Simulations" as const, id: "LIST" }],
    }),
    getSimulation: builder.query<SimulationResponse, SimulationScopedArgs>({
      query: ({ authoringDealerCode, simulationId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}`,
      }),
      providesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
      ],
    }),
    createSimulation: builder.mutation<
      SimulationResponse,
      DealerScopedArgs & { body: SimulationRequest }
    >({
      query: ({ authoringDealerCode, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Simulations", id: "LIST" }],
    }),
    updateSimulation: builder.mutation<
      SimulationResponse,
      SimulationScopedArgs & { body: SimulationRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
        { type: "Simulations" as const, id: "LIST" },
      ],
    }),
    deleteSimulation: builder.mutation<void, SimulationScopedArgs>({
      query: ({ authoringDealerCode, simulationId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Simulations", id: "LIST" }],
    }),
    submitSimulation: builder.mutation<
      SimulationResponse,
      SimulationScopedArgs & { body: SubmitSimulationRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/submit`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
        { type: "Simulations" as const, id: "LIST" },
      ],
    }),
    completeSimulation: builder.mutation<
      SimulationResponse,
      SimulationScopedArgs & { body: ApproveSimulationRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/complete`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
        { type: "Simulations" as const, id: "LIST" },
      ],
    }),
    approveSimulation: builder.mutation<
      SimulationResponse,
      SimulationScopedArgs & { body: ApproveSimulationRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
        { type: "PalRules" as const, id: "LIST" },
      ],
    }),
    rejectSimulation: builder.mutation<
      SimulationResponse,
      SimulationScopedArgs & { body: RejectSimulationRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/reject`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Simulations" as const, id: arg.simulationId },
      ],
    }),
    getSimulationRules: builder.query<
      ConfigurationRuleResponse[],
      SimulationScopedArgs
    >({
      query: ({ authoringDealerCode, simulationId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/rules`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result
                .filter((rule) => Boolean(rule.simulationRuleId))
                .map((rule) => ({
                  type: "SimulationRules" as const,
                  id: rule.simulationRuleId as string,
                })),
              { type: "SimulationRules" as const, id: "LIST" },
            ]
          : [{ type: "SimulationRules" as const, id: "LIST" }],
    }),
    getSimulationRule: builder.query<ConfigurationRuleResponse, RuleScopedArgs>(
      {
        query: ({ authoringDealerCode, simulationId, ruleId }) => ({
          url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/rules/${ruleId}`,
        }),
        providesTags: (_, __, arg) => [
          { type: "SimulationRules" as const, id: arg.ruleId },
        ],
      },
    ),
    addSimulationRule: builder.mutation<
      ConfigurationRuleResponse,
      SimulationScopedArgs & { body: ConfigurationRuleRequest }
    >({
      query: ({ authoringDealerCode, simulationId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/rules`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "SimulationRules", id: "LIST" }],
    }),
    updateSimulationRule: builder.mutation<
      ConfigurationRuleResponse,
      RuleScopedArgs & { body: ConfigurationRuleRequest }
    >({
      query: ({ authoringDealerCode, simulationId, ruleId, body }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/rules/${ruleId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "SimulationRules" as const, id: arg.ruleId },
        { type: "SimulationRules" as const, id: "LIST" },
      ],
    }),
    deleteSimulationRule: builder.mutation<void, RuleScopedArgs>({
      query: ({ authoringDealerCode, simulationId, ruleId }) => ({
        url: `/controltower/v1/dealers/${authoringDealerCode}/simulations/${simulationId}/rules/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "SimulationRules", id: "LIST" }],
    }),
    getOperators: builder.query<OperatorResponse[], GetOperatorsArgs | void>({
      query: (args) => ({
        url: `/controltower/v1/metadata/operators`,
        params: args
          ? buildQueryParams({ dataType: args.dataType })
          : undefined,
      }),
      providesTags: [{ type: "Metadata", id: "OPERATORS" }],
    }),
    getFieldDefinitions: builder.query<
      FieldDefinitionResponse[],
      GetFieldDefinitionsArgs
    >({
      query: ({
        authoringDealerCode,
        workstream,
        dataType,
        onlyWithAllowedValues,
      }) => ({
        url: `/controltower/v1/metadata/dealers/${authoringDealerCode}/fieldDefinitions`,
        params: buildQueryParams({
          workstream,
          dataType,
          onlyWithAllowedValues,
        }),
      }),
      providesTags: [{ type: "Metadata", id: "FIELDS" }],
    }),
    getFieldDefinitionWorkstreams: builder.query<string[], DealerScopedArgs>({
      query: ({ authoringDealerCode }) => ({
        url: `/controltower/v1/metadata/dealers/${authoringDealerCode}/fieldDefinitions/workstreams`,
      }),
      providesTags: [{ type: "Metadata", id: "FIELD_WORKSTREAMS" }],
    }),
    getFieldDefinition: builder.query<
      FieldDefinitionResponse,
      GetFieldDefinitionArgs
    >({
      query: ({ authoringDealerCode, fieldName }) => ({
        url: `/controltower/v1/metadata/dealers/${authoringDealerCode}/fieldDefinitions/${encodeURIComponent(
          fieldName,
        )}`,
      }),
      providesTags: (_, __, arg) => [
        { type: "Metadata" as const, id: `FIELD-${arg.fieldName}` },
      ],
    }),
    getDataTypes: builder.query<string[], void>({
      query: () => ({
        url: `/controltower/v1/metadata/dataTypes`,
      }),
      providesTags: [{ type: "Metadata", id: "DATA_TYPES" }],
    }),
    // User Roles endpoints
    getUserRoles: builder.query<UserRoleResponse, GetUserRolesArgs>({
      query: ({ userName, dealerCode }) => ({
        url: `/controltower/v1/users/${encodeURIComponent(
          userName,
        )}/dealers/${dealerCode}/roles`,
      }),
      providesTags: (_, __, arg) => [
        { type: "UserRoles" as const, id: `${arg.userName}-${arg.dealerCode}` },
      ],
    }),
    checkUserRole: builder.query<boolean, CheckUserRoleArgs>({
      query: ({ userName, dealerCode, roleName }) => ({
        url: `/controltower/v1/users/${encodeURIComponent(
          userName,
        )}/dealers/${dealerCode}/roles/${encodeURIComponent(roleName)}/check`,
      }),
      providesTags: (_, __, arg) => [
        {
          type: "UserRoles" as const,
          id: `${arg.userName}-${arg.dealerCode}-${arg.roleName}`,
        },
      ],
    }),
    // Get all dealers for a user
    getUserDealers: builder.query<UserDealersResponse, GetUserDealersArgs>({
      query: ({ userName }) => ({
        url: `/controltower/v1/users/${encodeURIComponent(userName)}/dealers`,
      }),
      providesTags: (_, __, arg) => [
        { type: "UserRoles" as const, id: `${arg.userName}-dealers` },
      ],
    }),

    // ==================== Administration Endpoints ====================

    // Get all PAL types
    getPalTypes: builder.query<PalTypeResponse[], void>({
      query: () => ({
        url: `/controltower/v1/admin/pal-types`,
      }),
      providesTags: [{ type: "PalTypes" as const, id: "LIST" }],
    }),

    // Get PAL type by ID
    getPalType: builder.query<PalTypeResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/controltower/v1/admin/pal-types/${id}`,
      }),
      providesTags: (_, __, arg) => [{ type: "PalTypes" as const, id: arg.id }],
    }),

    // Get all data source mappings, optionally filtered by PAL type
    getDataSourceMappings: builder.query<
      DataSourceMappingResponse[],
      { palType?: string } | void
    >({
      query: (args) => ({
        url: `/controltower/v1/admin/data-source-mappings`,
        params: args && args.palType ? { palType: args.palType } : undefined,
      }),
      providesTags: (result, _, args) =>
        result
          ? [
              ...result.map((mapping) => ({
                type: "DataSourceMappings" as const,
                id: mapping.id,
              })),
              {
                type: "DataSourceMappings" as const,
                id: args && args.palType ? `PAL_TYPE_${args.palType}` : "LIST",
              },
            ]
          : [
              {
                type: "DataSourceMappings" as const,
                id: args && args.palType ? `PAL_TYPE_${args.palType}` : "LIST",
              },
            ],
    }),

    // Get data source mapping by ID
    getDataSourceMapping: builder.query<
      DataSourceMappingResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/controltower/v1/admin/data-source-mappings/${id}`,
      }),
      providesTags: (_, __, arg) => [
        { type: "DataSourceMappings" as const, id: arg.id },
      ],
    }),

    // Get data source mappings by PAL type
    getDataSourceMappingsByPalType: builder.query<
      DataSourceMappingResponse[],
      { palTypeId: number }
    >({
      query: ({ palTypeId }) => ({
        url: `/controltower/v1/admin/pal-types/${palTypeId}/data-source-mappings`,
      }),
      providesTags: (_, __, arg) => [
        {
          type: "DataSourceMappings" as const,
          id: `PAL_TYPE_${arg.palTypeId}`,
        },
      ],
    }),

    // Get data source mappings by dealer
    getDataSourceMappingsByDealer: builder.query<
      DataSourceMappingResponse[],
      { dealerCode: string }
    >({
      query: ({ dealerCode }) => ({
        url: `/controltower/v1/admin/dealers/${dealerCode}/data-source-mappings`,
      }),
      providesTags: (_, __, arg) => [
        { type: "DataSourceMappings" as const, id: `DEALER_${arg.dealerCode}` },
      ],
    }),

    // Create data source mapping
    createDataSourceMapping: builder.mutation<
      DataSourceMappingResponse,
      DataSourceMappingRequest & { userName?: string }
    >({
      query: ({ sourceName, palType, databaseName, schemaName, userName }) => ({
        url: `/controltower/v1/admin/data-source-mappings`,
        method: "POST",
        body: { sourceName, palType, databaseName, schemaName },
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: [{ type: "DataSourceMappings", id: "LIST" }],
    }),

    // Delete data source mapping
    deleteDataSourceMapping: builder.mutation<
      void,
      { id: number; userName?: string }
    >({
      query: ({ id, userName }) => ({
        url: `/controltower/v1/admin/data-source-mappings/${id}`,
        method: "DELETE",
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "DataSourceMappings", id: arg.id },
        { type: "DataSourceMappings", id: "LIST" },
      ],
    }),

    // ==================== Helios Data Sources (Step 4) ====================

    // Get all schema/view combinations from HELIOS_NEXUS database
    getHeliosDataSources: builder.query<HeliosDataSourceResponse[], void>({
      query: () => ({
        url: `/controltower/v1/admin/helios-data-sources`,
      }),
      providesTags: [{ type: "HeliosDataSources", id: "LIST" }],
    }),

    // Get all dealers from HELIOS_NEXUS database for Applicable Dealers dropdown
    getDealers: builder.query<DealerDetailsResponse[], void>({
      query: () => ({
        url: `/controltower/v1/admin/dealers`,
      }),
      providesTags: [{ type: "Dealers", id: "LIST" }],
    }),

    // Get column metadata for a specific view (Step 6)
    getViewColumnMetadata: builder.query<
      ColumnMetadataResponse[],
      { schemaName: string; viewName: string }
    >({
      query: ({ schemaName, viewName }) => ({
        url: `/controltower/v1/admin/helios-data-sources/${encodeURIComponent(schemaName)}/${encodeURIComponent(viewName)}/columns`,
      }),
      providesTags: (_, __, arg) => [
        {
          type: "HeliosDataSources",
          id: `COLUMNS_${arg.schemaName}_${arg.viewName}`,
        },
      ],
    }),

    // Get operators by data type (Step 6)
    getOperatorsByDataType: builder.query<
      OperatorResponse[],
      { dataType?: string }
    >({
      query: ({ dataType }) => ({
        url: `/controltower/v1/admin/operators`,
        params: dataType ? { dataType } : undefined,
      }),
      providesTags: (_, __, arg) => [
        { type: "Metadata", id: `OPERATORS_${arg.dataType || "ALL"}` },
      ],
    }),

    // ==================== Attribute Mappings (Step 5) ====================

    // Get attribute mappings by data source mapping ID
    getAttributeMappingsByDataSource: builder.query<
      AttributeMappingResponse[],
      { mappingId: number }
    >({
      query: ({ mappingId }) => ({
        url: `/controltower/v1/admin/data-source-mappings/${mappingId}/attributes`,
      }),
      providesTags: (_, __, arg) => [
        { type: "AttributeMappings", id: `DATA_SOURCE_${arg.mappingId}` },
      ],
    }),

    // Get all attribute mappings, optionally filtered by PAL type
    getAttributeMappings: builder.query<
      AttributeMappingResponse[],
      { palType?: string }
    >({
      query: ({ palType }) => ({
        url: `/controltower/v1/admin/attribute-mappings`,
        params: palType ? { palType } : undefined,
      }),
      providesTags: (result, _, arg) =>
        result
          ? [
              ...result.map((attr) => ({
                type: "AttributeMappings" as const,
                id: attr.id,
              })),
              { type: "AttributeMappings" as const, id: arg.palType || "LIST" },
            ]
          : [{ type: "AttributeMappings" as const, id: arg.palType || "LIST" }],
    }),

    // Create attribute mapping
    createAttributeMapping: builder.mutation<
      AttributeMappingResponse,
      { mappingId: number; body: AttributeMappingRequest; userName?: string }
    >({
      query: ({ mappingId, body, userName }) => ({
        url: `/controltower/v1/admin/data-source-mappings/${mappingId}/attributes`,
        method: "POST",
        body,
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "AttributeMappings", id: `DATA_SOURCE_${arg.mappingId}` },
        { type: "AttributeMappings", id: "LIST" },
      ],
    }),

    // Update attribute mapping
    updateAttributeMapping: builder.mutation<
      AttributeMappingResponse,
      { attributeId: number; body: AttributeMappingRequest; userName?: string }
    >({
      query: ({ attributeId, body, userName }) => ({
        url: `/controltower/v1/admin/attribute-mappings/${attributeId}`,
        method: "PUT",
        body,
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "AttributeMappings", id: arg.attributeId },
        { type: "AttributeMappings", id: "LIST" },
      ],
    }),

    // Delete attribute mapping
    deleteAttributeMapping: builder.mutation<void, { attributeId: number }>({
      query: ({ attributeId }) => ({
        url: `/controltower/v1/admin/attribute-mappings/${attributeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "AttributeMappings", id: arg.attributeId },
        { type: "AttributeMappings", id: "LIST" },
      ],
    }),

    // ==================== Rule Mappings (Step 10) ====================

    // Get all rule mappings, optionally filtered by PAL type
    getRuleMappings: builder.query<RuleMappingResponse[], { palType?: string }>(
      {
        query: ({ palType }) => ({
          url: `/controltower/v1/admin/rule-mappings`,
          params: palType ? { palType } : undefined,
        }),
        providesTags: (result, _, arg) =>
          result
            ? [
                ...result.map((rule) => ({
                  type: "RuleMappings" as const,
                  id: rule.id,
                })),
                { type: "RuleMappings" as const, id: arg.palType || "LIST" },
              ]
            : [{ type: "RuleMappings" as const, id: arg.palType || "LIST" }],
      },
    ),

    // Create rule mapping
    createRuleMapping: builder.mutation<
      RuleMappingResponse,
      { body: RuleMappingRequest; userName?: string }
    >({
      query: ({ body, userName }) => ({
        url: `/controltower/v1/admin/rule-mappings`,
        method: "POST",
        body,
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: [{ type: "RuleMappings", id: "LIST" }],
    }),

    // Update rule mapping
    updateRuleMapping: builder.mutation<
      RuleMappingResponse,
      { ruleId: number; body: RuleMappingRequest; userName?: string }
    >({
      query: ({ ruleId, body, userName }) => ({
        url: `/controltower/v1/admin/rule-mappings/${ruleId}`,
        method: "PUT",
        body,
        headers: userName ? { "X-User-Name": userName } : undefined,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "RuleMappings", id: arg.ruleId },
        { type: "RuleMappings", id: "LIST" },
      ],
    }),

    // Delete rule mapping
    deleteRuleMapping: builder.mutation<void, { ruleId: number }>({
      query: ({ ruleId }) => ({
        url: `/controltower/v1/admin/rule-mappings/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "RuleMappings", id: arg.ruleId },
        { type: "RuleMappings", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPalRulesQuery,
  useGetPalRuleQuery,
  useGetPalRuleHistoryQuery,
  useGetAllPalRulesHistoryQuery,
  useGetPalWorkstreamsQuery,
  useGetPalDescriptionsQuery,
  useGetPalRuleCountQuery,
  useGetPalRulesAsOfDateQuery,
  useGetSimulationsQuery,
  useGetSimulationQuery,
  useCreateSimulationMutation,
  useUpdateSimulationMutation,
  useDeleteSimulationMutation,
  useSubmitSimulationMutation,
  useCompleteSimulationMutation,
  useApproveSimulationMutation,
  useRejectSimulationMutation,
  useGetSimulationRulesQuery,
  useLazyGetSimulationRulesQuery,
  useGetSimulationRuleQuery,
  useAddSimulationRuleMutation,
  useUpdateSimulationRuleMutation,
  useDeleteSimulationRuleMutation,
  useGetOperatorsQuery,
  useGetFieldDefinitionsQuery,
  useGetFieldDefinitionWorkstreamsQuery,
  useGetFieldDefinitionQuery,
  useGetDataTypesQuery,
  useGetUserRolesQuery,
  useCheckUserRoleQuery,
  useGetUserDealersQuery,
  // Administration hooks
  useGetPalTypesQuery,
  useGetPalTypeQuery,
  useGetDataSourceMappingsQuery,
  useGetDataSourceMappingQuery,
  useGetDataSourceMappingsByPalTypeQuery,
  useGetDataSourceMappingsByDealerQuery,
  useCreateDataSourceMappingMutation,
  useDeleteDataSourceMappingMutation,
  // Step 4 - Helios Data Sources
  useGetHeliosDataSourcesQuery,
  // Dealers
  useGetDealersQuery,
  // Step 6 - Column Metadata and Operators
  useGetViewColumnMetadataQuery,
  useLazyGetViewColumnMetadataQuery,
  useGetOperatorsByDataTypeQuery,
  useLazyGetOperatorsByDataTypeQuery,
  // Step 5 - Attribute Mappings
  useGetAttributeMappingsByDataSourceQuery,
  useGetAttributeMappingsQuery,
  useCreateAttributeMappingMutation,
  useUpdateAttributeMappingMutation,
  useDeleteAttributeMappingMutation,
  // Step 10 - Rule Mappings
  useGetRuleMappingsQuery,
  useCreateRuleMappingMutation,
  useUpdateRuleMappingMutation,
  useDeleteRuleMappingMutation,
} = dcmApi;
